import { Component, Suspense, useEffect, useRef, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { SessionProvider, useSession } from "./lib/session";
import { ErrorBox, PageLoading, ToastProvider } from "./components/ui";
import { lazyPage } from "./lib/lazyPage";
import { AppShell } from "./layouts/AppShell";
import { DemoPanel } from "./layouts/DemoPanel";
import { TermRoot } from "./lib/term";

const Landing = lazyPage(() => import("./pages/Landing"));
const AgentCheck = lazyPage(() => import("./pages/AgentCheck"));
const Login = lazyPage(() => import("./pages/Login"));
const LoginConfirm = lazyPage(() => import("./pages/LoginConfirm"));
const AccessRevoked = lazyPage(() => import("./pages/AccessRevoked"));
const Privacy = lazyPage(() => import("./pages/Privacy"));
const DataDeleted = lazyPage(() => import("./pages/DataDeleted"));
const Survey = lazyPage(() => import("./pages/Survey"));
const Intake = lazyPage(() => import("./pages/Intake"));
const Training = lazyPage(() => import("./pages/Training"));
const Onboarding = lazyPage(() => import("./pages/app/Onboarding"));
const Overview = lazyPage(() => import("./pages/app/Overview"));
const Chat = lazyPage(() => import("./pages/app/Chat"));
const Register = lazyPage(() => import("./pages/app/Register"));
const Anketa = lazyPage(() => import("./pages/app/Anketa"));
const Pismenost = lazyPage(() => import("./pages/app/Pismenost"));
const Dokumenti = lazyPage(() => import("./pages/app/Dokumenti"));
const Profile = lazyPage(() => import("./pages/app/Profile"));
const Admin = lazyPage(() => import("./pages/Admin"));
const NotFound = lazyPage(() => import("./pages/NotFound"));

/** Requires a logged-in user; optionally an org (else sends to onboarding). */
function RequireAuth({ children, needOrg = true }: { children: ReactNode; needOrg?: boolean }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading || !me) return <PageLoading />;
  if (!me.user) {
    const next = loc.pathname + loc.search;
    return <Navigate to={`/prijava?next=${encodeURIComponent(next)}`} replace />;
  }
  // Agentcija staff without an own company start on the admin page, everyone else in onboarding
  if (needOrg && !me.org) return <Navigate to={me.home === "/admin" ? "/admin" : "/app/zacetek"} replace />;
  return <>{children}</>;
}

/** Pilot access removed (403 {code: "revoked"}): one calm screen instead of any page,
 *  until the person goes somewhere else (e.g. the "Prijava" link in the header). */
function RevokedGate({ children }: { children: ReactNode }) {
  const { revoked, clearRevoked } = useSession();
  const loc = useLocation();
  const shownAt = useRef<string | null>(null);
  useEffect(() => {
    if (!revoked) {
      shownAt.current = null;
      return;
    }
    if (shownAt.current === null) shownAt.current = loc.key;
    else if (shownAt.current !== loc.key) clearRevoked();
  }, [revoked, loc.key, clearRevoked]);
  return revoked ? <AccessRevoked /> : <>{children}</>;
}

/** A page that fails to load or render shows an error with "try again" instead of a blank page.
 *  Going to another page clears the error. */
class PageErrorBoundary extends Component<{ resetKey: string; children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  componentDidUpdate(prev: { resetKey: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="container" style={{ paddingBlock: "48px" }}>
        <ErrorBox error={this.state.error} onRetry={() => window.location.reload()} />
      </main>
    );
  }
}

function PageErrors({ children }: { children: ReactNode }) {
  const loc = useLocation();
  return <PageErrorBoundary resetKey={loc.key}>{children}</PageErrorBoundary>;
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <ToastProvider>
          <PageErrors>
            <Suspense fallback={<PageLoading />}>
              <RevokedGate>
                <TermRoot>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/preveri" element={<AgentCheck />} />
                    <Route path="/s/:token" element={<AgentCheck builder />} />
                    <Route path="/prijava" element={<Login />} />
                    <Route path="/prijava/potrdi" element={<LoginConfirm />} />
                    <Route path="/zasebnost" element={<Privacy />} />
                    <Route path="/izbrisano" element={<DataDeleted />} />
                    <Route path="/a/:token" element={<Survey />} />
                    <Route path="/p/:token" element={<Intake />} />
                    <Route path="/u/:token" element={<Training />} />
                    <Route
                      path="/app/zacetek"
                      element={
                        <RequireAuth needOrg={false}>
                          <Onboarding />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/app"
                      element={
                        <RequireAuth>
                          <AppShell />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<Overview />} />
                      <Route path="pogovor" element={<Chat />} />
                      <Route path="register" element={<Register />} />
                      <Route path="register/:id" element={<Register />} />
                      <Route path="anketa" element={<Anketa />} />
                      <Route path="pismenost" element={<Pismenost />} />
                      <Route path="dokumenti" element={<Dokumenti />} />
                      <Route path="profil" element={<Profile />} />
                    </Route>
                    <Route
                      path="/admin"
                      element={
                        <RequireAuth needOrg={false}>
                          <Admin />
                        </RequireAuth>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </TermRoot>
              </RevokedGate>
            </Suspense>
          </PageErrors>
          <DemoPanel />
        </ToastProvider>
      </SessionProvider>
    </BrowserRouter>
  );
}
